// src/pages/SignaturCanvas.jsx
// Touch-/Maus-Signatur als Canvas. Gibt PNG als Base64 zurueck.

import { useRef, useState, useEffect } from 'react';
import { C } from './formui';
import { t } from '../lib/sprache';

export default function SignaturCanvas({ value, onChange, disabled = false, height = 180 }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(!!value);
  // Was dieses Feld selbst zuletzt abgegeben hat. Kommt genau das als value
  // zurueck, NICHT neu zeichnen: Vorher wurde nach jedem Federstrich die
  // halb so grosse Kopie ueber das Bild gemalt - die Unterschrift wurde mit
  // jedem Strich unschaerfer, und ein schneller zweiter Strich verschwand.
  const eigenerStand = useRef(undefined);

  // Bestehende Signatur (von aussen) in Canvas zeichnen
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    if (value && value === eigenerStand.current) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, c.width, c.height);
        setHasContent(true);
      };
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    let clientX, clientY;
    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX; clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX; clientY = e.clientY;
    }
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
    setHasContent(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const bild = exportiere();
    eigenerStand.current = bild;
    if (onChange) onChange(bild);
  };

  // Zum Speichern auf die halbe Kantenlaenge herunterrechnen. Gezeichnet wird
  // weiter fein (900 Punkte breit), aber uebertragen wird nur ein Viertel der
  // Bildpunkte. Fuer eine Unterschrift im PDF reicht das voellig, und im Feld
  // mit LTE ist das Hochladen der spuerbare Teil.
  const exportiere = () => {
    const c = canvasRef.current;
    const klein = document.createElement('canvas');
    klein.width = Math.round(c.width / 2);
    klein.height = Math.round(c.height / 2);
    const ctx = klein.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, klein.width, klein.height);
    ctx.drawImage(c, 0, 0, klein.width, klein.height);
    return klein.toDataURL('image/png');
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasContent(false);
    if (onChange) onChange(null);
  };

  return (
    <div>
      <div style={{ position: 'relative', border: '2px dashed ' + C.gray300, borderRadius: 8, background: C.white, overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          width={900}
          height={height * 3}
          style={{ width: '100%', height: height + 'px', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair', background: C.white, display: 'block' }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasContent && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: C.gray500, fontSize: 15, fontStyle: 'italic' }}>
            {t('Hier mit Finger oder Stift unterschreiben')}
          </div>
        )}
      </div>
      {!disabled && (
        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
          <button type='button' onClick={clear} disabled={!hasContent}
            style={{ minHeight: 44, padding: '8px 14px', background: hasContent ? C.redBg : C.gray100, color: hasContent ? C.red : C.gray500, border: 'none', borderRadius: 8, cursor: hasContent ? 'pointer' : 'not-allowed', fontSize: 15, fontWeight: 700 }}>
            {t('Signatur löschen')}
          </button>
        </div>
      )}
    </div>
  );
}